import { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Card, Alert, TextField, InputAdornment,
  Chip, IconButton, Tooltip, Button, Snackbar, CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SyncIcon from '@mui/icons-material/Sync';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';

import {
  useCvus,
  useCvuConfig,
  useDesactivarCvu,
  useReactivarCvu,
  useSincronizarCvus,
  useTestConexion,
} from '../../hooks/useCvus';
import {
  type Cvu,
  type CvuEstado,
  getEstadoCvuLabel,
  getEstadoCvuColor,
  formatCvu,
} from '../../types/cvu';
import { formatCuit } from '../../utils/format';
import GenerarCvuDialog from './GenerarCvuDialog';
import GenerarLoteDialog from './GenerarLoteDialog';

export default function CvusList() {
  const { data: cvusResult, isError, isLoading, error } = useCvus();
  const { data: config } = useCvuConfig();
  const desactivar = useDesactivarCvu();
  const reactivar  = useReactivarCvu();
  const sync       = useSincronizarCvus();
  const test       = useTestConexion();

  const [busqueda, setBusqueda] = useState('');
  const [openGenerar, setOpenGenerar] = useState(false);
  const [openLote, setOpenLote] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const cvus = cvusResult?.cvus ?? [];
  const esMock = config?.mode === 'mock';

  const filtered = useMemo(() => {
    if (!busqueda.trim()) return cvus;
    const q = busqueda.toLowerCase();
    return cvus.filter((c) =>
      c.empresa?.razon_social?.toLowerCase().includes(q) ||
      c.empresa?.cuit?.toLowerCase().includes(q) ||
      c.cvu?.toLowerCase().includes(q) ||
      c.alias?.toLowerCase().includes(q),
    );
  }, [cvus, busqueda]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleToggle = async (cvu: Cvu) => {
    try {
      if (cvu.enabled) {
        const r = await desactivar.mutateAsync(cvu.empresa_id);
        setSnackbar(r.message);
      } else {
        const r = await reactivar.mutateAsync(cvu.empresa_id);
        setSnackbar(r.message);
      }
    } catch (e) {
      setSnackbar((e as Error).message);
    }
  };

  const handleSync = async () => {
    try {
      const report = await sync.mutateAsync();
      setSnackbar(
        `Sincronización OK — Local: ${report.total_local} · Remoto: ${report.total_remoto} · ` +
        `Faltan local: ${report.en_remoto_sin_local.length} · Huérfanos local: ${report.en_local_sin_remoto.length} · ` +
        `Alias completados: ${report.alias_actualizados ?? 0}`
      );
    } catch (e) {
      setSnackbar('Error al sincronizar: ' + (e as Error).message);
    }
  };

  const handleTestConn = async () => {
    try {
      const r = await test.mutateAsync();
      setSnackbar(`Conexión OK (modo ${r.mode})`);
    } catch (e) {
      setSnackbar('Conexión falló: ' + (e as Error).message);
    }
  };

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columns: GridColDef<Cvu>[] = [
    {
      field: 'empresa',
      headerName: 'Empresa',
      flex: 1.5,
      minWidth: 200,
      renderCell: (p: GridRenderCellParams<Cvu>) => (
        <Stack spacing={0}>
          <Typography variant="body2">{p.row.empresa?.razon_social ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCuit(p.row.empresa?.cuit)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'cvu',
      headerName: 'CVU',
      width: 230,
      renderCell: (p: GridRenderCellParams<Cvu>) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
          {formatCvu(p.row.cvu)}
        </Typography>
      ),
    },
    {
      field: 'alias',
      headerName: 'Alias',
      width: 170,
      renderCell: (p: GridRenderCellParams<Cvu>) => (
        <Typography variant="body2" fontFamily="monospace">
          {p.row.alias}
        </Typography>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (p: GridRenderCellParams<Cvu, CvuEstado>) => (
        <Chip
          label={getEstadoCvuLabel(p.row.estado)}
          color={getEstadoCvuColor(p.row.estado)}
          size="small"
        />
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Cvu>) => {
        const pending = desactivar.isPending || reactivar.isPending;
        return p.row.enabled ? (
          <Tooltip title="Desactivar CVU">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleToggle(p.row)}
              disabled={pending}
              aria-label="Desactivar CVU"
            >
              <PowerSettingsNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Reactivar CVU">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleToggle(p.row)}
              disabled={pending}
              aria-label="Reactivar CVU"
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={2}
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5" fontWeight={600}>
            Gestión de CVUs
          </Typography>
          {config && (
            <Chip
              label={`Modo ${config.mode.toUpperCase()}`}
              size="small"
              color={esMock ? 'warning' : 'success'}
              variant={esMock ? 'filled' : 'outlined'}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              test.isPending ? <CircularProgress size={16} /> : <WifiTetheringIcon />
            }
            onClick={handleTestConn}
            disabled={test.isPending}
          >
            Probar conexión
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={sync.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={sync.isPending}
          >
            Sincronizar
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<GroupAddIcon />}
            onClick={() => setOpenLote(true)}
          >
            Generar lote
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenGenerar(true)}
          >
            Generar CVU
          </Button>
        </Stack>
      </Stack>

      {/* Banner modo mock */}
      {esMock && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Modo MOCK activo:</strong> las CVUs que generes son sintéticas
          (deterministas por CUIT) y no llegan al servidor de Pronto Pago.
          Para usar la API real, completá <code>PRONTO_PAGO_PUBLIC_KEY</code> y
          <code> PRONTO_PAGO_PRIVATE_KEY</code> en <code>api/.env</code>.
        </Alert>
      )}

      {/* Filtro */}
      <TextField
        size="small"
        placeholder="Buscar por empresa, CUIT, CVU o alias…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 380 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar CVUs: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'Aún no hay CVUs generadas' }}
        />
      </Card>

      {/* Dialogs */}
      <GenerarCvuDialog
        open={openGenerar}
        onClose={() => setOpenGenerar(false)}
        onSuccess={(c, w) => setSnackbar(w ?? `CVU generada: ${c.alias}`)}
      />
      <GenerarLoteDialog
        open={openLote}
        onClose={() => setOpenLote(false)}
        onSuccess={(r) => setSnackbar(
          `Lote ${r.id_lote}: ${r.generadas} generada(s), ${r.omitidas} omitida(s)` +
          (r.sin_alias && r.sin_alias.length > 0
            ? ` — sin alias del proveedor: ${r.sin_alias.join(', ')}`
            : '')
        )}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
